/**
 * sqlite3mt WAL Checksum — §96 §7.10 (bit-exact SQLite match)
 *
 * Consolidated from the legacy walChecksum.ts into sqlite3mt so that
 * the multi-worker runtime is self-contained.  Implements walChecksumBytes
 * from wal.c L856-892.
 *
 * Cumulative checksum used for:
 *   1. WAL header checksum (bytes 0..23 → stored at bytes 24..31)
 *   2. Frame checksum (frame_header[0..7] + page_data → stored at frame[16..23])
 *
 * Endianness determined by WAL magic:
 *   0x377f0682 → native (little-endian on x86/WASM)
 *   0x377f0683 → big-endian
 *
 * @see wal.c L856 (walChecksumBytes)
 * @see §96 §7.10 (WAL valid-prefix scan)
 */

// ── WAL Magic Constants ─────────────────────────────────────────

/** WAL magic: native byte order (little-endian on WASM/x86) */
export const WAL_MAGIC_LE = 0x377f0682

/** WAL magic: big-endian byte order */
export const WAL_MAGIC_BE = 0x377f0683

/** WAL format version (3007000 = 3.7.0, when WAL was introduced) */
export const WAL_FORMAT_VERSION = 3007000

/** WAL header size (32 bytes) */
export const WAL_HDR_SIZE = 32

/** WAL frame header size (24 bytes) */
export const WAL_FRAME_HDR_SIZE = 24

// ── WAL Header Structure ────────────────────────────────────────

export interface WalHeader {
    /** WAL magic number (determines endianness) */
    magic: number
    /** Format version (3007000) */
    version: number
    /** Page size */
    pageSize: number
    /** Checkpoint sequence number */
    nCkpt: number
    /** Salt values (random, changes on WAL reset) */
    salt1: number
    salt2: number
    /** Header checksum */
    cksum1: number
    cksum2: number
    /** true = native (LE on WASM), false = big-endian */
    nativeCksum: boolean
}

// ── Core Checksum Algorithm ─────────────────────────────────────

/**
 * Bit-exact SQLite WAL checksum (wal.c L856-892).
 *
 * Processes data in pairs of u32 words. The data length MUST be
 * a multiple of 8 bytes (this is guaranteed by SQLite for all
 * WAL header and frame data).
 *
 * @param nativeCksum true for native (LE) byte order, false for BE
 * @param data data bytes to checksum
 * @param aIn seed checksum [s1, s2] from previous frame (or [0, 0])
 * @returns [s1, s2] cumulative checksum
 */
export function walChecksumBytes(
    nativeCksum: boolean,
    data: Uint8Array,
    aIn: [number, number],
): [number, number] {
    const nByte = data.length
    if (nByte % 8 !== 0) {
        throw new Error(`walChecksumBytes: data length ${nByte} not multiple of 8`)
    }

    let s1 = aIn[0] >>> 0  // force unsigned
    let s2 = aIn[1] >>> 0

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
    const littleEndian = nativeCksum

    for (let i = 0; i < nByte; i += 8) {
        const x0 = view.getUint32(i, littleEndian)
        const x1 = view.getUint32(i + 4, littleEndian)
        // wal.c L881-882:
        //   s1 += *aData++ + s2;
        //   s2 += *aData++ + s1;
        s1 = (s1 + x0 + s2) >>> 0
        s2 = (s2 + x1 + s1) >>> 0
    }

    return [s1, s2]
}

// ── WAL Header Parsing ──────────────────────────────────────────

/**
 * Parse WAL header from raw bytes.
 * @param data at least 32 bytes (WAL header)
 * @returns parsed header, or null if invalid
 */
export function parseWalHeader(data: Uint8Array): WalHeader | null {
    if (data.length < WAL_HDR_SIZE) return null

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
    const magic = view.getUint32(0, false)  // magic is always BE in file

    let nativeCksum: boolean
    if (magic === WAL_MAGIC_LE) {
        nativeCksum = true
    } else if (magic === WAL_MAGIC_BE) {
        nativeCksum = false
    } else {
        return null  // not a valid WAL header
    }

    return {
        magic,
        version: view.getUint32(4, false),
        pageSize: view.getUint32(8, false),
        nCkpt: view.getUint32(12, false),
        salt1: view.getUint32(16, false),
        salt2: view.getUint32(20, false),
        cksum1: view.getUint32(24, false),
        cksum2: view.getUint32(28, false),
        nativeCksum,
    }
}

/**
 * Compute the WAL header checksum (bytes 0..23 → output at 24..31).
 * This seeds the first frame checksum.
 */
export function computeWalHeaderChecksum(
    nativeCksum: boolean,
    headerBytes: Uint8Array,
): [number, number] {
    // Checksum covers first 24 bytes of WAL header
    // (magic, version, pageSize, nCkpt, salt1, salt2)
    return walChecksumBytes(nativeCksum, headerBytes.subarray(0, 24), [0, 0])
}

/**
 * Build a complete WAL header (32 bytes).
 */
export function buildWalHeader(
    pageSize: number,
    nCkpt: number,
    salt1: number,
    salt2: number,
    nativeCksum: boolean = true,
): Uint8Array {
    const hdr = new Uint8Array(WAL_HDR_SIZE)
    const view = new DataView(hdr.buffer)

    const magic = nativeCksum ? WAL_MAGIC_LE : WAL_MAGIC_BE
    view.setUint32(0, magic, false)
    view.setUint32(4, WAL_FORMAT_VERSION, false)
    view.setUint32(8, pageSize, false)
    view.setUint32(12, nCkpt, false)
    view.setUint32(16, salt1, false)
    view.setUint32(20, salt2, false)

    // Compute checksum over first 24 bytes → store at 24..31
    const [c1, c2] = walChecksumBytes(nativeCksum, hdr.subarray(0, 24), [0, 0])
    view.setUint32(24, c1, false)
    view.setUint32(28, c2, false)

    return hdr
}

// ── Frame Checksum ──────────────────────────────────────────────

/**
 * Compute checksum for a WAL frame.
 *
 * Checksum covers: frame_header[0..7] (pgno + nTruncate) + page_data.
 * The running checksum from the previous frame (or WAL header) is the seed.
 *
 * @param nativeCksum endianness flag
 * @param pgno page number
 * @param nTruncate 0 for mid-tx spill, >0 for commit (= db page count)
 * @param pageData page content (pageSize bytes)
 * @param prevCksum running checksum from previous frame or WAL header
 * @returns [s1, s2] for this frame
 */
export function computeFrameChecksum(
    nativeCksum: boolean,
    pgno: number,
    nTruncate: number,
    pageData: Uint8Array,
    prevCksum: [number, number],
): [number, number] {
    // First: checksum the 8-byte frame header prefix (pgno + nTruncate)
    const hdr8 = new Uint8Array(8)
    const hdrView = new DataView(hdr8.buffer)
    hdrView.setUint32(0, pgno, false)      // pgno is always big-endian in WAL
    hdrView.setUint32(4, nTruncate, false)  // nTruncate is always big-endian

    const afterHdr = walChecksumBytes(nativeCksum, hdr8, prevCksum)

    // Then: checksum the page data (must be multiple of 8, pageSize always is)
    return walChecksumBytes(nativeCksum, pageData, afterHdr)
}

/**
 * Build a complete 24-byte WAL frame header.
 *
 * Layout (wal.c L960-993):
 *   [0..3]   pgno       (big-endian)
 *   [4..7]   nTruncate  (big-endian, 0=mid-tx, >0=commit+dbPageCount)
 *   [8..11]  salt1      (big-endian, must match WAL header)
 *   [12..15] salt2      (big-endian, must match WAL header)
 *   [16..19] cksum1     (big-endian, cumulative)
 *   [20..23] cksum2     (big-endian, cumulative)
 */
export function buildFrameHeader(
    pgno: number,
    nTruncate: number,
    salt1: number,
    salt2: number,
    cksum: [number, number],
): Uint8Array {
    const hdr = new Uint8Array(WAL_FRAME_HDR_SIZE)
    const view = new DataView(hdr.buffer)
    view.setUint32(0, pgno, false)
    view.setUint32(4, nTruncate, false)
    view.setUint32(8, salt1, false)
    view.setUint32(12, salt2, false)
    view.setUint32(16, cksum[0], false)
    view.setUint32(20, cksum[1], false)
    return hdr
}

/**
 * Read the running checksum from the last frame in a WAL buffer.
 *
 * @param walData WAL buffer (SAB₂ view)
 * @param mxFrame total number of frames (0 = use WAL header checksum)
 * @param pageSize page size
 * @returns [s1, s2] from last frame, or WAL header checksum if no frames
 */
export function readLastFrameChecksum(
    walData: Uint8Array,
    mxFrame: number,
    pageSize: number,
): [number, number] {
    if (mxFrame === 0) {
        // No frames — return WAL header checksum (seed for first frame)
        const hdrView = new DataView(walData.buffer, walData.byteOffset, WAL_HDR_SIZE)
        return [hdrView.getUint32(24, false), hdrView.getUint32(28, false)]
    }

    // Last frame starts at: WAL_HDR + (mxFrame-1) * frameSize
    const frameSize = WAL_FRAME_HDR_SIZE + pageSize
    const lastFrameOff = WAL_HDR_SIZE + (mxFrame - 1) * frameSize
    const view = new DataView(walData.buffer, walData.byteOffset + lastFrameOff, WAL_FRAME_HDR_SIZE)
    return [view.getUint32(16, false), view.getUint32(20, false)]
}
